<template>
  <div class="tm-paging-limit">
    <div
      v-if="!isMobile"
      class="tm-paging-limit__text"
    >
      {{ $t('tmTableFooter.rowsPerPage') }}
    </div>

    <field-select
      :model-value="perPageValue"
      :options="perPageOptions"
      :clearable="false"
      outlined
      dense
      size="xSmall"
      :menu-fit="false"
      @update:model-value="$emit('update:model-value', +$event)"
    >
      <template v-slot:selected-item="{ opt }: { opt: SelectOption }">
        <span class="font-weight-semi-bold white-space-nowrap">{{ opt.text ?? missingOptionText }}</span>
      </template>
    </field-select>
  </div>
</template>

<script setup lang="ts">
import { computed } from '@/composition/vue/compositionApi'
import { getTranslateService } from '@/core/container/ioc'
import type { SelectOption } from '@/services/forms/types'
import FieldSelect from '@/components/forms/fields/FieldSelect.vue'
import { useBreakpoints } from '@/composition/breakpoints'

const props = withDefaults(
  defineProps<{
    value?: number
    options?: number[]
  }>(),
  {
    value: 10,
    options: () => [10, 25, 50, 100, 250],
  },
)

defineEmits<{
  'update:model-value': [perPage: number]
}>()

const { isMobile } = useBreakpoints()
const translateService = getTranslateService()

const perPageValue = computed(() => String(props.value))

const perPageOptions = computed<SelectOption[]>(() =>
  props.options.map((num) => ({
    text: !num ? translateService.t('common.all') : translateService.tc('tmTableFooter.perPageOption', num),
    value: String(num),
  })),
)

const missingOptionText = computed<string | null>(() => {
  if (props.options.includes(props.value)) {
    return null
  }

  return translateService.tc('tmTableFooter.perPageOption', props.value)
})
</script>

<style lang="scss" scoped>
.tm-paging-limit {
  display: flex;
  align-items: center;

  &__text {
    margin-right: 10px;
    font-size: $sm-x-font;
    color: $gray8;
  }

  :deep(.tm-field-select) {
    .q-field__control,
    .q-field__native,
    .q-field__append {
      min-height: 32px !important;
      height: 32px !important;
    }
    .q-field__native {
      font-size: $sm-font;
      color: $neutral;
    }
    .q-field__append {
      i {
        font-size: $md-font;
        color: $gray8;
      }
    }
  }
  :deep(.q-menu) {
    line-height: 20px !important;
  }
}
</style>
